import { useEffect, useRef, useState, type ElementType, type ReactNode } from "react";
import { cn } from "@/lib/utils";

const VARIANTS = {
  up: "reveal",
  left: "reveal-left",
  zoom: "reveal-zoom",
} as const;

export function Reveal({
  children,
  className,
  as: Tag = "div",
  delay = 0,
  variant = "up",
}: {
  children: ReactNode;
  className?: string;
  as?: ElementType;
  delay?: number;
  variant?: keyof typeof VARIANTS;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={cn(VARIANTS[variant], visible && "is-visible", className)}
    >
      {children}
    </Tag>
  );
}

/** Overflow wrapper that used to JS-parallax; kept CSS-only to avoid forced reflow. */
export function MediaFrame({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("overflow-hidden", className)}>{children}</div>;
}
